import { skipToken } from "@reduxjs/toolkit/query";
import Guid from "_shared/guids/guid";
import { FilterCriteriaRow } from "_shared/_views/list-pages/filter-criteria-row";
import { useGetCompaniesByLegalEntityIdsQuery } from "marketplace/companies/_api/company-api";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import { useMemo } from "react";
import type WorkRecordCriteria from "work/work-records/work-record-criteria";
import type { ActiveWorkRecordCriterion } from "work/work-records/work-record-criteria";
import { WorkRecordCriterionKind, type WorkRecordCriterionValue } from "work/work-records/work-record-criterion";

/**
 * The criteria a list's URL sets, each a chip that removes it.
 *
 * A list the assistant links to (#6221) can be filtered by things the table has no control for —
 * who leads the vendor team, which firm is on the other side, a fee rate — so without these the
 * reader would see a shorter list and no reason for it. Each chip says one criterion; removing it
 * takes the criterion out of the URL, which is what the list is asked for, so the list follows.
 *
 * A person or a firm is named when the directory names them to this reader, and otherwise the chip
 * says only what the criterion is. An id is never shown. *Clear filters* takes them all off (#6714);
 * the row is the shared `FilterCriteriaRow`, as a page without these criteria draws its column filters.
 */
export default function WorkRecordCriteriaChips(
  props: Readonly<{
    criteria: WorkRecordCriteria;
    searchParams: URLSearchParams;
    onChange: (next: URLSearchParams) => void;
    /** Parameters a control on the page already shows, such as the search box's `query`. */
    shownElsewhere?: readonly string[];
  }>,
) {
  const { criteria, searchParams, onChange, shownElsewhere } = props;

  const active = useMemo(
    () =>
      criteria
        .active(searchParams)
        .filter(({ criterion }) => !(shownElsewhere ?? []).includes(criterion.parameter)),
    [criteria, searchParams, shownElsewhere],
  );

  // Stable sets, so each request is asked for once per set of people or firms rather than once per
  // render.
  const personUserIds = useMemo(() => idsOfKind(active, WorkRecordCriterionKind.Person), [active]);
  const firmLegalEntityIds = useMemo(() => idsOfKind(active, WorkRecordCriterionKind.Firm), [active]);
  const individualsQuery = useGetIndividualsByUserIdsQuery(
    personUserIds.size > 0 ? { userIds: [...personUserIds] } : skipToken,
  );
  const namesByUserId = useMemo(() => {
    const names = new Map<string, string>();
    for (const json of individualsQuery.data ?? []) {
      const individual = Individual.fromJSON(json);
      if (individual?.fullName) names.set(individual.userId.value, individual.fullName);
    }
    return names;
  }, [individualsQuery.data]);
  // A firm the reader may not see comes back `unavailable`, and its chip says only its role.
  const companiesQuery = useGetCompaniesByLegalEntityIdsQuery(
    firmLegalEntityIds.size > 0 ? { legalEntityIds: [...firmLegalEntityIds] } : skipToken,
  );
  const namesByLegalEntityId = useMemo(() => {
    const names = new Map<string, string>();
    for (const company of companiesQuery.data?.found ?? []) {
      const name = company.name?.trim();
      if (name) names.set(company.legalEntityId.toLowerCase(), name);
    }
    return names;
  }, [companiesQuery.data]);

  if (active.length === 0) return null;

  function nameOf(kind: WorkRecordCriterionKind, value: WorkRecordCriterionValue): string | undefined {
    if (!(value instanceof Guid)) return undefined;
    if (kind === WorkRecordCriterionKind.Person) return namesByUserId.get(value.value);
    if (kind === WorkRecordCriterionKind.Firm) return namesByLegalEntityId.get(value.value.toLowerCase());
    return undefined;
  }

  const items = active.map(({ criterion, value }) => ({
    key: criterion.parameter,
    text: criterion.describe(value, nameOf(criterion.kind, value)),
    onRemove: () => onChange(criteria.without(searchParams, criterion)),
  }));
  // Every chip off in one change, so the list is asked for once rather than once per chip.
  const clearAll = () =>
    onChange(active.reduce((params, { criterion }) => criteria.without(params, criterion), searchParams));
  return <FilterCriteriaRow items={items} onClearAll={clearAll} />;
}

/** The id of every active criterion of `kind`, as the API spells it. */
function idsOfKind(active: readonly ActiveWorkRecordCriterion[], kind: WorkRecordCriterionKind): Set<string> {
  const ids = new Set<string>();
  for (const { criterion, value } of active) {
    if (criterion.kind === kind && value instanceof Guid) ids.add(value.value);
  }
  return ids;
}
